import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

/*
 * Cometa Labs venture-studio process — Forward-style flywheel cards.
 * Each card has a small line-and-ball SVG animation (SMIL, no dependencies).
 * The active step auto-advances; hovering or using the arrows takes over.
 */

const ACCENT = '#4f8fff';
const INK = '#e8eaf0';
const MUTED = '#4a4a52';

const Ball = ({ r = 5, fill = ACCENT, children }) => (
  <circle r={r} fill={fill}>
    {children}
  </circle>
);

// 01 — Ideate & validate: idea and market circles, a ball shuttling between their centres
function IdeateArt({ active }) {
  const stroke = active ? INK : MUTED;
  return (
    <svg viewBox="0 0 240 160" className="of-studio-svg">
      <circle cx="88" cy="80" r="58" fill="none" stroke={stroke} strokeWidth="1" />
      <circle cx="152" cy="80" r="58" fill="none" stroke={ACCENT} strokeOpacity={active ? 0.9 : 0.45} strokeWidth="1" />
      <line x1="88" y1="80" x2="152" y2="80" stroke={stroke} strokeDasharray="2 4" strokeWidth="1" />
      <circle cx="88" cy="80" r="3" fill={ACCENT} opacity="0.5" />
      <circle cx="152" cy="80" r="3" fill={ACCENT} opacity="0.5" />
      <Ball r={4.5}>
        <animate attributeName="cx" values="88;152;88" dur="3.2s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.2 1;0.4 0 0.2 1" />
        <animate attributeName="cy" values="80;80;80" dur="3.2s" repeatCount="indefinite" />
      </Ball>
    </svg>
  );
}

// 02 — Build the MVP: a ring being drawn, core pulsing at the centre
function BuildArt({ active }) {
  const stroke = active ? INK : MUTED;
  return (
    <svg viewBox="0 0 240 160" className="of-studio-svg">
      <circle cx="120" cy="80" r="66" fill="none" stroke={stroke} strokeWidth="1.2" strokeDasharray="415" strokeDashoffset="0">
        <animate attributeName="stroke-dashoffset" values="415;0;0" keyTimes="0;0.7;1" dur="3.6s" repeatCount="indefinite" />
      </circle>
      <circle cx="120" cy="80" r="56" fill="none" stroke={stroke} strokeDasharray="2 4" strokeWidth="1" />
      <line x1="64" y1="80" x2="176" y2="80" stroke={stroke} strokeDasharray="2 4" strokeWidth="1" />
      <circle cx="120" cy="80" r="9" fill="none" stroke={ACCENT} strokeWidth="1.2">
        <animate attributeName="r" values="6;11;6" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="stroke-opacity" values="1;0.2;1" dur="2.4s" repeatCount="indefinite" />
      </circle>
      <circle cx="120" cy="80" r="4.5" fill={ACCENT} />
    </svg>
  );
}

// 03 — Refine strategy: two orbits with satellites iterating around the core
function RefineArt({ active }) {
  const stroke = active ? INK : MUTED;
  return (
    <svg viewBox="0 0 240 160" className="of-studio-svg">
      <circle cx="120" cy="80" r="66" fill="none" stroke={stroke} strokeDasharray="2 4" strokeWidth="1" />
      <circle cx="120" cy="80" r="46" fill="none" stroke={stroke} strokeDasharray="2 4" strokeWidth="1" />
      <circle cx="120" cy="80" r="4" fill={ACCENT} />
      <g>
        <animateTransform attributeName="transform" type="rotate" from="0 120 80" to="360 120 80" dur="9s" repeatCount="indefinite" />
        <circle cx="120" cy="14" r="4" fill={active ? '#c9cad1' : '#4a4a52'} />
      </g>
      <g>
        <animateTransform attributeName="transform" type="rotate" from="140 120 80" to="-220 120 80" dur="6s" repeatCount="indefinite" />
        <circle cx="120" cy="34" r="4" fill={active ? ACCENT : '#4a4a52'} />
      </g>
    </svg>
  );
}

// 04 — Go to market: a ball travels from the core out along a path to three markets
function LaunchArt({ active }) {
  const stroke = active ? INK : MUTED;
  const path = 'M 40 110 C 90 110, 110 50, 200 50';
  return (
    <svg viewBox="0 0 240 160" className="of-studio-svg">
      <path d={path} fill="none" stroke={stroke} strokeDasharray="2 4" strokeWidth="1" />
      <path d="M 40 110 L 200 110" fill="none" stroke={stroke} strokeOpacity="0.35" strokeDasharray="2 4" strokeWidth="1" />
      <circle cx="40" cy="110" r="6" fill="none" stroke={stroke} strokeWidth="1" />
      {[50, 80, 110].map((y, i) => (
        <circle key={y} cx="200" cy={y} r="5" fill="none" stroke={i === 0 ? ACCENT : stroke} strokeWidth="1" opacity={i === 0 ? 1 : 0.5} />
      ))}
      <Ball r={4.5}>
        <animateMotion dur="3s" repeatCount="indefinite" path={path} keyPoints="0;1;1" keyTimes="0;0.75;1" calcMode="linear" />
      </Ball>
      <circle cx="200" cy="50" r="5" fill="none" stroke={ACCENT} strokeWidth="1">
        <animate attributeName="r" values="5;5;16" keyTimes="0;0.75;1" dur="3s" repeatCount="indefinite" />
        <animate attributeName="stroke-opacity" values="0;0.9;0" keyTimes="0;0.75;1" dur="3s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// 05 — Scale: ripples expanding from the core
function ScaleArt({ active }) {
  const stroke = active ? INK : MUTED;
  return (
    <svg viewBox="0 0 240 160" className="of-studio-svg">
      <circle cx="120" cy="80" r="66" fill="none" stroke={stroke} strokeWidth="1" />
      <line x1="54" y1="80" x2="186" y2="80" stroke={stroke} strokeDasharray="2 4" strokeWidth="1" />
      {[0, 1, 2].map((i) => (
        <circle key={i} cx="120" cy="80" r="6" fill="none" stroke={ACCENT} strokeWidth="1">
          <animate attributeName="r" values="6;64" dur="3.6s" begin={`${i * 1.2}s`} repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" values="0.9;0" dur="3.6s" begin={`${i * 1.2}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <circle cx="120" cy="80" r="4.5" fill={ACCENT} />
    </svg>
  );
}

export const STUDIO_STEPS = [
  { key: 'ideate', label: 'Ideate & Validate', text: 'We identify promising markets and unmet needs, then validate the opportunity through research, rapid experimentation, and ecosystem feedback.', Art: IdeateArt },
  { key: 'build', label: 'Build the MVP', text: 'Our product and engineering teams design the architecture, prototype quickly, and build a functional MVP ready for real users and real world testing.', Art: BuildArt },
  { key: 'refine', label: 'Test & Refine', text: 'We put products in front of users, measure traction, and use real feedback and data to refine the product, business model, and positioning before scaling.', Art: RefineArt },
  { key: 'launch', label: 'Launch & Spin Out', text: 'Once the product shows potential, we develop its brand and go-to-market strategy, recruit dedicated leadership, establish the venture structure, and prepare it for independent growth.', Art: LaunchArt },
  { key: 'scale', label: 'Scale', text: 'We help ventures grow through fundraising, strategic partnerships, talent, marketing, and access to the Draper ecosystem while continuing to provide strategic and technical support.', Art: ScaleArt },
];

export default function StudioProcess() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setActive((a) => (a + 1) % STUDIO_STEPS.length), 4200);
    return () => clearInterval(t);
  }, [paused]);

  // keep the active card in view on narrow screens
  useEffect(() => {
    const track = trackRef.current;
    const card = track?.children?.[active];
    if (!track || !card || track.scrollWidth <= track.clientWidth) return;
    track.scrollTo({ left: card.offsetLeft - track.offsetLeft - 16, behavior: 'smooth' });
  }, [active]);

  const go = (d) => {
    setPaused(true);
    setActive((a) => (a + d + STUDIO_STEPS.length) % STUDIO_STEPS.length);
  };

  return (
    <div>
      <div
        ref={trackRef}
        className="of-studio-track"
        onMouseLeave={() => setPaused(false)}
      >
        {STUDIO_STEPS.map((s, i) => {
          const isActive = i === active;
          return (
            <button
              type="button"
              key={s.key}
              className={`of-studio-card ${isActive ? 'is-active' : ''}`}
              onMouseEnter={() => { setPaused(true); setActive(i); }}
              onFocus={() => { setPaused(true); setActive(i); }}
              onClick={() => { setPaused(true); setActive(i); }}
              aria-pressed={isActive}
            >
              <span className="of-studio-index">{String(i + 1).padStart(2, '0')}</span>
              <s.Art active={isActive} />
              <span className="of-studio-label">{s.label}</span>
              <span className="of-studio-text">{s.text}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-6 flex items-center justify-center gap-2">
        <button type="button" onClick={() => go(-1)} className="of-studio-nav" aria-label="Previous step"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex items-center gap-1.5 mx-2">
          {STUDIO_STEPS.map((s, i) => (
            <span key={s.key} className={`of-studio-dot ${i === active ? 'is-active' : ''}`} />
          ))}
        </div>
        <button type="button" onClick={() => go(1)} className="of-studio-nav" aria-label="Next step"><ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
